/* Copyright 2019 Ellucian Company L.P. and its affiliates.
* File: ApplicationHandler.tsx
* Type: Presentation component */

// #region Imports
import React from 'react';
import Media from 'react-media';

// Core components
import Button from '@powercampus/design-system/react/core/Button';
import Card from '@powercampus/design-system/react/core/Card';
import Divider from '@powercampus/design-system/react/core/Divider';
import Grid from '@powercampus/design-system/react/core/Grid';
import Icon from '@powercampus/design-system/react/core/Icon';
import MessageStyled from '@powercampus/design-system/react/core/MessageStyled';
import StepProgress, {
    MobileStepper,
    MobileStepperLabel,
    MobileStepperStep,
    MobileStepperTitle,
    Step,
    StepButton,
    StepContent,
    StepLabel
} from '@powercampus/design-system/react/core/StepProgress';
import Text from '@powercampus/design-system/react/core/Text';
import TextLink from '@powercampus/design-system/react/core/TextLink';

// Internal components
import ApplicationCompletedModal from './ApplicationCompletedModal';
import FieldsGroupHandler from './FieldsGroupHandler';

// Helpers
import Format from '@powercampus/design-system/helpers/Format';
import Tokens from '@powercampus/design-system/react/core/styles/Tokens';
import { createStyles, Theme, withStyles, WithStyles } from '@powercampus/design-system/react/core/styles/withStyles';

// Types
import { IDropDownOption } from '@powercampus/design-system/types/IDropDownOption';
import { IApplicationContent } from '../../../Types/Applications/IApplicationContent';
import { IApplicationErrors } from '../../../Types/Applications/IApplicationErrors';
import { IApplicationForm } from '../../../Types/Form/IApplicationForm';
import { IFieldsGroup } from '../../../Types/Form/IFieldsGroup';
import { IPaymentTransaction } from '../../../Types/Payment/IPaymentTransaction';
import { IApplicationHandler } from '../../../Types/Resources/Admissions/IApplicationFormResources';
// #endregion

// #region Internal types
export interface IApplicationHandlerProps {
    activeStep: number;
    components?: IApplicationForm;
    dateTimeCulture: string;
    errors: IApplicationErrors[];
    expanded: boolean | string;
    failedPayment: boolean;
    firstDayOfWeek: number;
    paymentTransaction: IPaymentTransaction;
    saveMessage?: string;
    section?: JSON;
    shortDatePattern: string;
    showSaveButton: boolean;
    successPayment: boolean;

    resources: IApplicationHandler;
    onAddMore: (fieldGroup: IFieldsGroup) => void;
    onBackStep: () => void;
    onClickSave?: () => void;
    onClickStep: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onChangeCheckBox: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onChangeDateTimeField: (date: string, id: string, isValid: boolean) => void;
    onChangeDropDown: (optionSelected: IDropDownOption, id: string) => void;
    onChangeExpansionPanel: (label: string) => void;
    onChangeListCheckbox: (event: any) => void;
    onChangeRadioGroup: (event: React.ChangeEvent<any>, value: string) => void;
    onChangeTextField: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onClickButton: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onCloseFailedPayment: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onDeleteItem: (event: any) => void;

    onNextStep: () => void;
    onSubmit: () => void;
}

const styles = (theme: Theme) => createStyles({
    backButtonMovil: {
        marginLeft: Tokens.spacingXSmall
    },
    boxStep: {
        '& > span > span > span': {
            textAlign: 'left'
        },
        '&:focus': {
            boxShadow: 'none'
        }
    },
    buttonContainer: {
        marginTop: Tokens.spacingMedium
    },
    buttonsContainerMovil: {
        marginRight: Tokens.spacingMedium
    },
    buttonStep: {
        marginRight: Tokens.spacingSmall
    },
    cardRoot: {
        [theme.breakpoints.up('sm')]: {
            marginTop: Tokens.spacingXxLarge
        }
    },
    fieldContainer: {
        marginTop: Tokens.spacingXSmall
    },
    footerPadding: {
        padding: `${Tokens.spacingMedium} ${Tokens.spacingMedium} 0 ${Tokens.spacingMedium}`,
        textAlign: 'center'
    },
    inline: {
        display: 'inline'
    },
    marginLeft: {
        display: 'inline',
        marginLeft: Tokens.sizingSmall
    },
    mobileStepperContent: {
        minHeight: '65vh',
        padding: `${Tokens.spacingSmall} ${Tokens.spacingSmall} 0 ${Tokens.spacingSmall}`
    },
    saveButtonMargin: {
        marginLeft: Tokens.sizingSmall,
        marginRight: Tokens.sizingSmall
    },
    saveFooter: {
        textAlign: 'center'
    },
    stepperDesktop: {
        padding: `${Tokens.spacingMedium} ${Tokens.spacingMedium} 0 ${Tokens.spacingMedium}`
    }
});

type PropsWithStyles = IApplicationHandlerProps & WithStyles<typeof styles>;
// #endregion

// #region Component

const ApplicationHandler: React.FC<PropsWithStyles> = (props: PropsWithStyles): JSX.Element => {
    const {
        activeStep,
        classes,
        components,
        dateTimeCulture,
        errors,
        expanded,
        failedPayment,
        firstDayOfWeek,
        saveMessage,
        paymentTransaction,
        shortDatePattern,
        showSaveButton,
        successPayment,

        onAddMore,
        onBackStep,
        onClickSave,
        onClickStep,
        onChangeCheckBox,
        onChangeDateTimeField,
        onChangeDropDown,
        onChangeExpansionPanel,
        onChangeListCheckbox,
        onChangeRadioGroup,
        onChangeTextField,
        onClickButton,
        onCloseFailedPayment,
        onDeleteItem,
        onNextStep,
        onSubmit,

        resources
    } = props;

    const noContent: JSX.Element = (
        <MessageStyled
            classMessage="noResults"
            message={resources.lblNotAvailable}
        />
    );

    let content: IApplicationContent[] = [];
    if (components && components.steps && components.steps.length > 0) {
        content = Array(components.steps.length);
        components.steps.forEach((step, i) => {
            const fieldsGroupsElements: JSX.Element[] = [];
            if (step.fieldsGroups && step.fieldsGroups.length > 0) {
                step.fieldsGroups.forEach((fieldGroup, j) => {
                    fieldsGroupsElements.push(
                        <FieldsGroupHandler
                            dateTimeCulture={dateTimeCulture}
                            errors={errors}
                            expanded={expanded}
                            fieldGroup={fieldGroup}
                            isHorizontalAligned={fieldGroup.isHorizontalAligned}
                            isExpansionPanel={fieldGroup.isExpansionPanel}
                            fieldGroupIndex={j}
                            firstDayOfWeek={firstDayOfWeek}
                            key={`fieldGroup|${j}|${i}`}
                            stepIndex={i}
                            shortDatePattern={shortDatePattern}
                            onChangeDateTimeField={onChangeDateTimeField}
                            onDeleteItem={onDeleteItem}
                            resources={resources}
                            onAddMore={onAddMore}
                            onChangeCheckBox={onChangeCheckBox}
                            onChangeDropDown={onChangeDropDown}
                            onChangeExpansionPanel={onChangeExpansionPanel}
                            onChangeListCheckbox={onChangeListCheckbox}
                            onChangeRadioGroup={onChangeRadioGroup}
                            onChangeTextField={onChangeTextField}
                            onClickButton={onClickButton}
                        />
                    );
                });
            }

            content[i] = {
                content: (
                    <Grid container key={`stepContent|${i}`} className={classes.fieldContainer}>
                        <Grid item xs>
                            {fieldsGroupsElements}
                        </Grid>
                    </Grid>),
                instructions: step.instructions,
                title: step.title
            };
        });
    }

    const fieldsGroupsModal: JSX.Element[] = [];
    const footer: JSX.Element[] = [];
    if (components && components.fieldsGroups) {
        if (components.fieldsGroups.length > 0) {
            // Confirmation Dialog
            if (components.isCompletedApplication) {
                let confirmationGroup: IFieldsGroup | undefined;
                const confirmationIndex: number =
                    components.fieldsGroups.findIndex(x => x.id === 'confirmationGroup');
                if (confirmationIndex > -1) {
                    confirmationGroup = components.fieldsGroups[confirmationIndex];
                }

                fieldsGroupsModal.push(
                    <ApplicationCompletedModal
                        key="CompletedModal"
                        dateTimeCulture={dateTimeCulture}
                        failedPayment={failedPayment}
                        fieldGroup={confirmationGroup}
                        paymentTransaction={paymentTransaction}
                        shortDatePattern={shortDatePattern}
                        successPayment={successPayment}
                        onAddMore={onAddMore}
                        onChangeCheckBox={onChangeCheckBox}
                        onChangeDateTimeField={onChangeDateTimeField}
                        onChangeDropDown={onChangeDropDown}
                        onChangeListCheckbox={onChangeListCheckbox}
                        onChangeRadioGroup={onChangeRadioGroup}
                        onChangeTextField={onChangeTextField}
                        onClickButton={onClickButton}
                        onCloseFailedPayment={onCloseFailedPayment}
                        resourcesApplicationHandler={resources}
                    />
                );
            }
            // Footer
            let footerGroup: IFieldsGroup | undefined;
            const footerGroupIndex: number =
                components.fieldsGroups.findIndex(x => x.id === 'footerGroup');
            if (footerGroupIndex > -1) {
                footerGroup = components.fieldsGroups[footerGroupIndex];
            }
            if (footerGroup) {
                footer.push(
                    <FieldsGroupHandler
                        dateTimeCulture={dateTimeCulture}
                        fieldGroup={footerGroup}
                        fieldGroupIndex={1}
                        isExpansionPanel={footerGroup.isExpansionPanel}
                        isHorizontalAligned={footerGroup.isHorizontalAligned}
                        stepIndex={1}
                        resources={resources}
                        onAddMore={onAddMore}
                        shortDatePattern={shortDatePattern}
                        onChangeCheckBox={onChangeCheckBox}
                        onChangeDateTimeField={onChangeDateTimeField}
                        onChangeDropDown={onChangeDropDown}
                        onChangeListCheckbox={onChangeListCheckbox}
                        onChangeRadioGroup={onChangeRadioGroup}
                        onChangeTextField={onChangeTextField}
                        onClickButton={onClickButton}
                    />
                );
            }
        }
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs>
                <div id="applicationTop" />
                <Card classes={{ root: classes.cardRoot }} accent="secondary">
                    {content.length > 0 ? (
                        <Media query={Tokens.mqXSmall}>
                            {(matches: boolean): JSX.Element => matches ? (
                                <MobileStepper
                                    steps={content.length}
                                    position="static"
                                    activeStep={activeStep}
                                    nextButton={
                                        (
                                            <Button
                                                color="secondary"
                                                disabled={activeStep === content.length}
                                                id={'btnNext|mobile'}
                                                fluid={false}
                                                size="small"
                                                onClick={onNextStep}
                                            >
                                                {resources.lblNext}
                                            </Button>
                                        )
                                    }
                                    backButton={
                                        (
                                            <Button
                                                color="secondary"
                                                disabled={activeStep === 0}
                                                id={'btnBack|mobile'}
                                                fluid={false}
                                                size="small"
                                                onClick={onBackStep}
                                            >
                                                {resources.lblBack}
                                            </Button>
                                        )
                                    }
                                >
                                    <div className={classes.mobileStepperContent}>
                                        <MobileStepperTitle>
                                            <>
                                                {components ? (
                                                    <>
                                                        {components.name || ''}
                                                        {components.enableOnlinePayment
                                                            && components.feeAmount ?
                                                            (
                                                                <Text>
                                                                    {Format.toString(components.isFlatFee
                                                                        ? resources.formatHasFee
                                                                        : resources.formatHasFees,
                                                                        [components.feeAmount])}
                                                                </Text>
                                                            )
                                                            : undefined}
                                                    </>
                                                ) : undefined}
                                            </>
                                        </MobileStepperTitle>

                                        {activeStep !== content.length ? (
                                            <>
                                                <MobileStepperStep activeStep={activeStep}>
                                                    <MobileStepperLabel>
                                                        {content[activeStep].title}
                                                    </MobileStepperLabel>
                                                </MobileStepperStep>
                                                {content[activeStep].content}
                                            </>
                                        ) : (
                                                <>
                                                    <StepProgress nonLinear activeStep={activeStep} orientation="vertical">
                                                        {content.map((step, i) => (
                                                            <Step key={`step|${i}`}>
                                                                <StepButton
                                                                    classes={{ root: classes.boxStep }}
                                                                    id={`step|${i}`}
                                                                    onClick={onClickStep}
                                                                >
                                                                    <StepLabel
                                                                        error={errors[i].isStepError}
                                                                        optional={step.instructions}
                                                                    >
                                                                        {step.title}
                                                                    </StepLabel>
                                                                </StepButton>
                                                            </Step>
                                                        ))}
                                                    </StepProgress>
                                                    <Button
                                                        id="btn"
                                                        onClick={onSubmit}
                                                    >
                                                        {resources.lblSubmit}
                                                    </Button>
                                                </>
                                            )
                                        }
                                    </div>
                                </MobileStepper>
                            ) : (
                                    <>
                                        {components ? (
                                            <Grid container className={classes.stepperDesktop}>
                                                <Grid item xs>
                                                    <Text size="h2">
                                                        {components.name || ''}
                                                    </Text>
                                                    {components.enableOnlinePayment
                                                        && components.feeAmount ?
                                                        (
                                                            <Text>
                                                                {Format.toString(components.isFlatFee
                                                                    ? resources.formatHasFee
                                                                    : resources.formatHasFees, [components.feeAmount])}
                                                            </Text>
                                                        )
                                                        : undefined}
                                                    <Divider />
                                                </Grid>
                                            </Grid>
                                        ) : undefined}
                                        <StepProgress nonLinear activeStep={activeStep} orientation="vertical">
                                            {content.map((step, i) => (
                                                <Step key={`step|${i}`}>
                                                    <StepButton
                                                        classes={{ root: classes.boxStep }}
                                                        id={`step|${i}`}
                                                        onClick={onClickStep}
                                                    >
                                                        <StepLabel error={errors[i].isStepError} optional={step.instructions}>
                                                            {step.title}
                                                        </StepLabel>
                                                    </StepButton>
                                                    <StepContent>
                                                        {step.content}
                                                        <div className={classes.buttonContainer}>
                                                            <TextLink
                                                                className={classes.buttonStep}
                                                                disabled={activeStep === 0}
                                                                id={`btnBack|${i}`}
                                                                onClick={onBackStep}
                                                                text={resources.lblBack}
                                                            />
                                                            <Button
                                                                className={classes.buttonStep}
                                                                color="primary"
                                                                id={`btnNext|${i}`}
                                                                onClick={activeStep === content.length - 1
                                                                    ? onSubmit : onNextStep}
                                                            >
                                                                {activeStep === content.length - 1
                                                                    ? resources.lblSubmit
                                                                    : resources.lblNext}
                                                            </Button>
                                                        </div>
                                                    </StepContent>
                                                </Step>
                                            ))}
                                        </StepProgress >
                                    </>
                                )
                            }
                        </Media>
                    ) : noContent}
                    {showSaveButton && onClickSave ?
                        (
                            <div className={classes.saveFooter}>
                                <br />
                                <Grid item xs={12}>
                                    <Text>
                                        {saveMessage}
                                    </Text>
                                </Grid>
                                <br />
                                <div className={classes.saveButtonMargin}>
                                    <Button
                                        className={classes.inline}
                                        color="secondary"
                                        id="btnSave"
                                        onClick={onClickSave}
                                    >
                                        <Icon name="save" />
                                        <div className={classes.marginLeft}>
                                            {resources.lblSave}
                                        </div>
                                    </Button>
                                </div>
                            </div>
                        )
                        : undefined
                    }
                    <div className={classes.footerPadding}>
                        {footer}
                    </div>
                    {fieldsGroupsModal}
                </Card>
            </Grid>
        </Grid >
    );
};
// #endregion

// Export: Component
export default withStyles(styles)(ApplicationHandler);